import React, {FC} from 'react';

import {cn} from '@bem-react/classname';
import {Loader} from '@gravity-ui/uikit';
import {QuestionImageSize} from 'common/constants/survey';
import {isEmpty} from 'lodash';
import {useParams, useSearchParams} from 'react-router-dom';
import {withErrorBoundary} from 'ui/hocs/withAppProviders';
import useDocumentTitle from 'ui/hooks/useDocumentTitle';
import useFooter from 'ui/hooks/useFooter';
import usePrepareData from 'ui/hooks/usePrepareData';
import useStyles from 'ui/hooks/useStyles';
import useSuccessRedirect from 'ui/hooks/useSuccessRedirect';
import {actions, selectors, useAppDispatch, useAppSelector} from 'ui/store';

import Integrations from '../Integrations';
import Promo from '../Promo';
import Results from '../Results';
import ShareComponent from '../Share';
import Stats from '../Stats';
import SuccessMessage from '../SuccessMessage';
import {getSurveyId} from '../SurveyPage/SurveyPage.utils';

import {getShowFlags} from './SuccessPage.utils';

import './SuccessPage.scss';

const {selectSuccess} = selectors;
const {prepareSuccess, prepareIntegrations} = actions;

export const cnSuccessPage = cn('SuccessPage');
export const {iframe} = window.YC;

const Share = withErrorBoundary(ShareComponent);

const SuccessPage: FC = () => {
    const {surveyId = ''} = useParams();
    const [searchParams] = useSearchParams();
    const answerKey = searchParams.get('akey');
    const shareResult = searchParams.get('share');

    const dispatch = useAppDispatch();
    const isPreparedData = usePrepareData(
        [
            prepareSuccess({surveyId: getSurveyId(surveyId), answerKey}),
            prepareIntegrations({answerKey}),
        ],
        dispatch,
    );

    const successData = useAppSelector(selectSuccess);

    const [buttonPaymentUrl] = useSuccessRedirect(answerKey, successData, isPreparedData);

    useDocumentTitle(successData.name);

    useStyles(successData.styles);

    useFooter(isPreparedData, successData.footer);

    if (!isPreparedData || !answerKey) {
        return (
            <div className={cnSuccessPage('Loader')}>
                <Loader size="l" />
            </div>
        );
    }

    const show = getShowFlags(shareResult, successData);

    return (
        <div className={cnSuccessPage('', {iframe})}>
            {show.message && (
                <SuccessMessage
                    image={successData.image}
                    scores={successData.scores}
                    totalScores={successData.total_scores}
                    title={successData.title}
                    subtitle={successData.subtitle}
                    redirect={successData.redirect}
                    buttonPaymentUrl={buttonPaymentUrl}
                    fillAgain={successData.fill_again}
                />
            )}
            {show.share && (
                <Share
                    surveyId={surveyId}
                    stats={!isEmpty(successData.stats)}
                    results={successData.results}
                    answerKey={answerKey}
                    scores={successData.scores}
                    totalScores={successData.total_scores}
                    title={successData.title}
                    surveyName={successData.name}
                    imageLink={successData.image?.links?.[QuestionImageSize.width360]}
                />
            )}

            {show.results && <Results answerKey={answerKey} />}

            {show.stats && successData.stats && <Stats stats={successData.stats} />}

            {show.integrations && <Integrations answerKey={answerKey} />}

            {show.promo && <Promo />}
        </div>
    );
};

export default SuccessPage;
